import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'

type Props = {
  open: boolean
  url: string | null
  title?: string
  onClose: () => void
}

export default function ImageFullscreenViewer({ open, url, title, onClose }: Props) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open || !url) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-white p-4 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.06)]"
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Image preview'}
    >
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 pb-3 text-gray-900">
        <p className="truncate text-sm font-medium">{title || 'Preview'}</p>
        <button
          type="button"
          className="rounded p-2 text-gray-700 hover:bg-gray-100"
          onClick={onClose}
          aria-label="Close"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>
      <div className="mt-3 min-h-0 flex-1 overflow-auto bg-white">
        <div className="flex min-h-full justify-center p-2">
          <img
            src={url}
            alt=""
            className="h-auto w-auto max-w-none object-none"
            style={{ maxHeight: 'calc(100vh - 6rem)', maxWidth: 'min(100%, 100vw)' }}
          />
        </div>
      </div>
    </div>,
    document.body
  )
}
